import { ContextType } from 'api/conversations';
import { HttpClient } from 'api/http-client';
import { ProjectId, Timestamp } from 'api/models';
import { ConversationContext } from 'data/conversation';

interface BaseProjectGlobalConversation {
  entityId: number;
  entityType: ContextType;
  entityName: string;
  unreadMessages: number;
  lastCommentCreatedTime: Timestamp;
  private: boolean;
  contextType: ConversationContext['type'];
  displayEntityType: 'tasks' | 'space-tabs' | 'status-updates';
  spaceTab: never;
  statusUpdate: never;
}
interface SpaceMeta {
  space: {
    spaceId: number;
  };
  spaceTabId: number;
  metaData: {
    icon?: string;
  };
}
interface StatusUpdate {
  statusUpdateId: number;
}

export interface ProjectGlobalConversationDocument
  extends Omit<BaseProjectGlobalConversation, 'spaceTab' | 'statusUpdate'> {
  entityType: ContextType.DOCUMENT;
  displayEntityType: 'space-tabs';
  spaceTab: SpaceMeta;
}

export interface ProjectGlobalConversationSpaceTab
  extends Omit<BaseProjectGlobalConversation, 'spaceTab' | 'statusUpdate'> {
  entityType: ContextType.SPACE_TAB;
  displayEntityType: 'space-tabs';
  spaceTab: SpaceMeta;
}

export interface ProjectGlobalConversationStatusUpdate
  extends Omit<BaseProjectGlobalConversation, 'spaceTab' | 'statusUpdate'> {
  entityType: ContextType.DOCUMENT;
  displayEntityType: 'status-updates';
  statusUpdate: StatusUpdate;
}
export type ProjectGlobalConversation =
  | ProjectGlobalConversationStatusUpdate
  | ProjectGlobalConversationDocument
  | ProjectGlobalConversationSpaceTab
  | BaseProjectGlobalConversation;

type ProjectGlobalConversationResponse = Omit<
  ProjectGlobalConversation,
  'contextType'
>;

export const getContextFromEntityType = (
  entityType: ProjectGlobalConversation['entityType'],
): ProjectGlobalConversation['contextType'] => {
  const entries = Object.entries(ContextType);

  return entries.find(
    ([, value]) => value === entityType,
  )?.[0] as ProjectGlobalConversation['contextType'];
};

const constructWithContextType = (
  responses: ProjectGlobalConversationResponse[],
): ProjectGlobalConversation[] => {
  return responses.map((res) => {
    const contextType = getContextFromEntityType(res.entityType);

    if (res.displayEntityType === 'space-tabs') {
      return {
        ...res,
        contextType: getContextFromEntityType(res.entityType),
        spaceTab: {
          ...(res as ProjectGlobalConversationDocument).spaceTab,
          metaData: {
            icon: (res as any)?.spaceTab?.metaData?.links?.icon?.find((ico) =>
              ico.type.includes('image/'),
            )?.href,
          },
        },
      } as ProjectGlobalConversationDocument;
    }

    if (contextType === 'DOCUMENT') {
      return {
        ...res,
        contextType: getContextFromEntityType(res.entityType),
      } as ProjectGlobalConversationStatusUpdate;
    }

    return {
      ...res,
      contextType: getContextFromEntityType(res.entityType),
    } as BaseProjectGlobalConversation;
  });
};

class ProjectGlobalConversationsHttpClient extends HttpClient {
  public async getRelevantChat([, projectID]: ProjectId): Promise<
    ProjectGlobalConversation[]
  > {
    return this.get<ProjectGlobalConversationResponse[]>(
      `/projects/${projectID}/global-conversations`,
    ).then<ProjectGlobalConversation[]>(constructWithContextType);
  }

  public async updateConversationLastSeen(
    entityType: ProjectGlobalConversation['entityType'],
    entityId: number,
    data: { lastSeenCommentCreatedAt: number },
  ): Promise<ProjectGlobalConversation> {
    return this.post(`${entityType}/${entityId}/conversation/lastSeen`, data);
  }
}

export const ProjectGlobalConversationsAPI =
  new ProjectGlobalConversationsHttpClient();
